import type { Cart } from "@dransay/api/models";
import isNil from "lodash/isNil";
import { ShippingProviderLabel } from "~/utils/shipping-provider-labels";

type VendorDeliveryMethod = NonNullable<
	Cart["vendor"]
>["deliveryMethods"][number];

/**
 * The carrier's display name, when it is one the platform knows how to label.
 *
 * Returns null rather than the raw value for an unknown provider: the enum is
 * a closed set the FE maps by hand, so an unmapped carrier would otherwise
 * surface its wire spelling to the customer.
 */
export function resolveProviderLabel(
	provider: VendorDeliveryMethod["provider"],
): string | null {
	if (!(provider && provider in ShippingProviderLabel)) {
		return null;
	}
	return ShippingProviderLabel[provider as keyof typeof ShippingProviderLabel];
}

/**
 * The day range behind the "Delivery time n - m days" line, or null when the
 * method carries no estimate. Only shipping methods do.
 *
 * Returns the range rather than the rendered string so the caller owns the
 * translation — the recap and the selectable card render the same numbers in
 * different places.
 */
export function resolveDeliveryTimeRange(
	method: VendorDeliveryMethod,
): { minDays: number; maxDays: number } | null {
	if (
		method.kind !== "shipping" ||
		isNil(method.minDays) ||
		isNil(method.maxDays)
	) {
		return null;
	}
	return { minDays: method.minDays, maxDays: method.maxDays };
}
